import type { FlowIOField, FlowActionParamField } from '@reflex/flows';

/**
 * One `(provider, operation)` action served by `GET /flow-action-catalog`,
 * filtered to the plugins the caller's org actually has installed.
 *
 * Every action with a registered handler appears. Every field is present on
 * the wire — this is a derived contract, not an authoring surface, so a
 * consumer never has to distinguish "the server omitted it" from "the action
 * declared nothing". Undeclared collapses to the empty value (`[]`, `false`,
 * `null`); the one exception is `outputs`, where `null` means "can't verify"
 * rather than "produces nothing" and the difference is load-bearing in the
 * graph validator.
 *
 * Powers the editor's action picker and the node inspector's derived I/O for
 * `action` nodes, whose outputs otherwise live only in the server-side
 * registry. Those two want different subsets — the picker offers only what a
 * person should author ({@link selectableActions}), the inspector must
 * resolve whatever a node already names — so the response carries both and
 * each surface narrows it.
 */
export interface FlowActionCatalogEntry {
  provider: string;
  operation: string;
  /** Declared output fields, or `null` when the action declared none. */
  outputs: FlowIOField[] | null;
  /**
   * Params the handler reads, from the action registry descriptor; empty when
   * it declared none.
   */
  params: FlowActionParamField[];
  /**
   * The plugin this action ships in, recorded by the host when the plugin
   * registered it. The server narrows this list by it — an org only gets
   * actions from plugins it has installed — and the web side carries it only
   * so a debugging surface can say where an action came from.
   *
   * `null` when the handler was registered outside any plugin, which every
   * org can run.
   */
  pluginName: string | null;
  /**
   * Engine plumbing rather than an authorable step (see the action
   * registry's `internal` descriptor flag). Served so the inspector can
   * explain a system flow's nodes; hidden from the picker.
   */
  internal: boolean;
  /**
   * Human-readable summary from the declaring plugin's `flowModels.actions`
   * entry. `null` when the plugin registered a handler but declared no flow
   * model — the picker falls back to the operation name.
   */
  description: string | null;
  /**
   * JSON Schema of the action's params model (from `flowModels.actions`), or
   * `null` when none is declared. Run surfaces use its `'<provider>:<kind>'`
   * fieldType annotations to badge the concrete ids an action ran with.
   */
  paramsJsonSchema: Record<string, unknown> | null;
}

/** Stable key for one catalog entry. */
export function actionKey(entry: Pick<FlowActionCatalogEntry, 'provider' | 'operation'>): string {
  return `${entry.provider}:${entry.operation}`;
}

/**
 * The actions a person may add to a flow: everything the org can run, less
 * the internal plumbing.
 *
 * Deliberately not applied by {@link findActionCatalogEntry} — a system
 * flow's node legitimately names an internal action, and the editor has to
 * render it as configured rather than flag it as missing.
 */
export function selectableActions(entries: FlowActionCatalogEntry[]): FlowActionCatalogEntry[] {
  return entries.filter((e) => !e.internal);
}

/**
 * Group entries by provider for the picker, providers sorted alphabetically.
 *
 * Operations are sorted within each group too — unlike the event catalog's
 * equivalent, which leaves registration order alone. The server sorts as
 * well; this keeps the menu stable even if a caller hands us entries in some
 * other order.
 */
export function groupActionCatalogByProvider(
  entries: FlowActionCatalogEntry[],
): Array<{ provider: string; entries: FlowActionCatalogEntry[] }> {
  const groups = new Map<string, FlowActionCatalogEntry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.provider);
    if (list) list.push(entry);
    else groups.set(entry.provider, [entry]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([provider, list]) => ({
      provider,
      entries: [...list].sort((a, b) => a.operation.localeCompare(b.operation)),
    }));
}

/** Find the catalog entry an action node is configured to run. */
export function findActionCatalogEntry(
  entries: FlowActionCatalogEntry[],
  config: { provider: string; operation: string },
): FlowActionCatalogEntry | undefined {
  return entries.find((e) => e.provider === config.provider && e.operation === config.operation);
}

/** Params an action refuses to run without, in declaration order. */
export function requiredActionParams(entry: FlowActionCatalogEntry | undefined): string[] {
  return (entry?.params ?? []).filter((p) => p.required).map((p) => p.key);
}
